import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { 
    Container, Box, Typography, TextField, Button, Alert, Paper, 
    Link, useTheme, InputAdornment, IconButton
} from '@mui/material';
import { Landscape, Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';

const Login = () => {
    const theme = useTheme();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const { login, error, user } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const success = await login(email, password);
        if (success) {
            // Role-based redirect:
            // - Workers go to /worker (React route)
            // - Managers/Admins go to /map/planner (server-rendered, full reload)
            const role = user?.role;
            // user state may not be updated yet; read from token directly
            const token = localStorage.getItem('token');
            if (token) {
                try {
                    const decoded = JSON.parse(atob(token.split('.')[1]));
                    if (decoded.role === 'worker') {
                        navigate('/worker', { replace: true });
                        return;
                    }
                } catch {}
            }
            window.location.href = '/map/planner';
        }
        setLoading(false);
    };

    return (
        <Box sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            alignItems: 'center', 
            bgcolor: '#F8FAFC',
            backgroundImage: 'radial-gradient(#E2E8F0 1px, transparent 1px)',
            backgroundSize: '20px 20px'
        }}>
            <Container component="main" maxWidth="xs">
                <Paper 
                    elevation={0} 
                    sx={{ 
                        p: 4, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        width: '100%',
                        border: '1px solid #E2E8F0',
                        borderRadius: 3,
                        bgcolor: 'white',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                >
                    <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Box 
                            sx={{ 
                                width: 56, 
                                height: 56, 
                                bgcolor: 'primary.main', 
                                borderRadius: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mb: 2,
                                boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)'
                            }}
                        >
                            <Landscape sx={{ color: 'white', fontSize: 32 }} />
                        </Box>
                        <Typography component="h1" variant="h5" fontWeight={800} color="text.primary" gutterBottom>
                            Welcome Back
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            Sign in to access your dashboard
                        </Typography>
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ width: '100%', mb: 3, borderRadius: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Email Address"
                            name="email"
                            autoComplete="email"
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 }
                            }}
                        />
                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="current-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock color="action" fontSize="small" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: { borderRadius: 2 }
                            }}
                        />
                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{ 
                                mt: 3, 
                                mb: 3, 
                                py: 1.5, 
                                fontWeight: 700, 
                                borderRadius: 2,
                                fontSize: '1rem',
                                boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                                '&:hover': {
                                    boxShadow: '0 10px 15px -3px rgba(37, 99, 235, 0.3)',
                                }
                            }}
                            disabled={loading}
                        >
                            {loading ? 'Signing In...' : 'Sign In'}
                        </Button>
                    </Box>
                    
                    <Box sx={{ textAlign: 'center', mb: 2, width: '100%' }}>
                        <Link 
                            component="button"
                            variant="body2" 
                            onClick={() => {
                                navigate('/forgot-password');
                            }}
                            underline="hover" 
                            color="text.secondary"
                        >
                            Forgot password?
                        </Link>
                    </Box>

                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                            Don't have an account?{' '}
                            <Link component={RouterLink} to="/signup" underline="hover" fontWeight={600} color="primary">
                                Sign Up
                            </Link>
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default Login;
